import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { NOMBRE_TIPO, poli } from './poliApi';

/**
 * P-13 Configuracion del modulo (Administrador de poligrafias): nombre, color y correo de cada
 * estado; listas (tipos, motivos, puestos, ciudades, fotos, preguntas) y campos adicionales del
 * formulario. Las reglas entre estados no se configuran: son fijas.
 */
const TIPOS_CAMPO = [
    { label: 'Texto', value: 'TEXTO' }, { label: 'Numero', value: 'NUMERO' }, { label: 'Fecha', value: 'FECHA' },
    { label: 'Lista', value: 'LISTA' }, { label: 'Si / No', value: 'SI_NO' }
];
const PASOS = [1, 2, 3, 4, 5, 6, 7].map((p) => ({ label: `Paso ${p}`, value: p }));

const PoliConfiguracion = () => {
    const toast = useRef(null);
    const [cfg, setCfg] = useState(null);
    const [tipo, setTipo] = useState('MOTIVO');
    const [compania, setCompania] = useState(null);
    const [nuevo, setNuevo] = useState('');
    const [campo, setCampo] = useState(null);

    const avisar = (severity, detail) => toast.current && toast.current.show({ severity, summary: severity === 'error' ? 'Error' : 'Guardado', detail, life: 3500 });

    const cargar = useCallback(async () => {
        try {
            setCfg(await poli('/config'));
        } catch (e) {
            avisar('error', e.message);
        }
    }, []);

    useEffect(() => { cargar(); }, [cargar]);

    const guardar = async (fn, mensaje) => {
        try {
            await fn();
            avisar('success', mensaje);
            cargar();
        } catch (e) {
            avisar('error', e.message);
        }
    };

    const lista = useMemo(() => ((cfg && cfg.catalogos) || [])
        .filter((c) => c.tipo === tipo && (tipo !== 'PUESTO' || c.compania_id === compania)), [cfg, tipo, compania]);

    if (!cfg) return <div className="card"><Toast ref={toast} />Cargando configuracion…</div>;
    if (!cfg.perfiles.includes('ADMIN')) return <div className="card">Esta pantalla es del Administrador de poligrafias.</div>;

    const companias = (cfg.companias || []).map((c) => ({ label: c.nombre, value: c.compania_id }));

    return (
        <div className="card">
            <Toast ref={toast} />
            <h4 className="mt-0">Configuracion del modulo de poligrafia</h4>
            <TabView>
                <TabPanel header="Estados">
                    <p className="text-600 mt-0">El nombre, el color y el aviso por correo se pueden cambiar. El orden y las reglas entre estados son fijos.</p>
                    <DataTable value={cfg.estados} size="small" editMode="row" dataKey="estado" responsiveLayout="scroll"
                        onRowEditComplete={(e) => guardar(() => poli(`/config/estados/${e.newData.estado}`, 'PUT',
                            { nombre: e.newData.nombre, color: e.newData.color, notificaCorreo: e.newData.notifica_correo }), `Estado ${e.newData.estado}`)}>
                        <Column field="estado" header="N.º" style={{ width: '4rem' }} />
                        <Column field="nombre" header="Nombre" editor={(o) => <InputText value={o.value} onChange={(e) => o.editorCallback(e.target.value)} />} />
                        <Column field="color" header="Color" body={(r) => <span><span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 3, background: r.color, verticalAlign: 'middle' }} /> {r.color}</span>}
                            editor={(o) => <input type="color" value={o.value} onChange={(e) => o.editorCallback(e.target.value)} aria-label="Color del estado" />} />
                        <Column field="notifica_correo" header="Avisa por correo" body={(r) => (r.notifica_correo ? 'Si' : 'No')}
                            editor={(o) => <InputSwitch checked={!!o.value} onChange={(e) => o.editorCallback(e.value)} />} />
                        <Column rowEditor headerStyle={{ width: '7rem' }} />
                    </DataTable>
                </TabPanel>
                <TabPanel header="Listas">
                    <div className="flex flex-wrap gap-2 align-items-end mb-3">
                        <div className="flex flex-column gap-1">
                            <label htmlFor="cfg-tipo">Lista</label>
                            <Dropdown inputId="cfg-tipo" value={tipo} options={Object.entries(NOMBRE_TIPO).map(([value, label]) => ({ value, label }))}
                                onChange={(e) => setTipo(e.value)} style={{ minWidth: '16rem' }} />
                        </div>
                        {tipo === 'PUESTO' && (
                            <div className="flex flex-column gap-1">
                                <label htmlFor="cfg-compania">Compania</label>
                                <Dropdown inputId="cfg-compania" value={compania} options={companias} placeholder="Elija la compania"
                                    onChange={(e) => setCompania(e.value)} style={{ minWidth: '14rem' }} />
                            </div>
                        )}
                        <div className="flex flex-column gap-1 flex-1" style={{ minWidth: '14rem' }}>
                            <label htmlFor="cfg-nuevo">Nuevo elemento</label>
                            <InputText id="cfg-nuevo" value={nuevo} onChange={(e) => setNuevo(e.target.value)} />
                        </div>
                        <Button label="Agregar" icon="pi pi-plus" disabled={!nuevo.trim() || (tipo === 'PUESTO' && !compania)}
                            onClick={() => guardar(async () => {
                                await poli('/config/catalogo', 'POST', { tipo, nombre: nuevo, companiaId: tipo === 'PUESTO' ? compania : null, orden: lista.length + 1 });
                                setNuevo('');
                            }, 'Elemento agregado')} />
                    </div>
                    <DataTable value={lista} size="small" editMode="row" dataKey="catalogo_id" emptyMessage="Lista vacia" responsiveLayout="scroll"
                        onRowEditComplete={(e) => guardar(() => poli(`/config/catalogo/${e.newData.catalogo_id}`, 'PUT',
                            { nombre: e.newData.nombre, orden: e.newData.orden }), 'Elemento actualizado')}>
                        <Column field="orden" header="Orden" style={{ width: '6rem' }} editor={(o) => <InputNumber value={o.value} onValueChange={(e) => o.editorCallback(e.value)} inputStyle={{ width: '4rem' }} />} />
                        <Column field="nombre" header="Nombre" editor={(o) => <InputText value={o.value} onChange={(e) => o.editorCallback(e.target.value)} style={{ width: '100%' }} />} />
                        <Column header="Activo" body={(r) => <InputSwitch checked={r.activo} aria-label={`Activar ${r.nombre}`}
                            onChange={(e) => guardar(() => poli(`/config/catalogo/${r.catalogo_id}`, 'PUT', { nombre: r.nombre, activo: e.value }), e.value ? 'Activado' : 'Desactivado')} />} />
                        <Column rowEditor headerStyle={{ width: '7rem' }} />
                    </DataTable>
                </TabPanel>
                <TabPanel header="Campos del formulario">
                    <div className="flex justify-content-between align-items-center mb-2">
                        <p className="text-600 m-0">Campos adicionales que el poligrafista llena en el paso indicado.</p>
                        <Button label="Nuevo campo" icon="pi pi-plus" onClick={() => setCampo({ paso: 2, tipo: 'TEXTO', obligatorio: false, activo: true, orden: 0 })} />
                    </div>
                    <DataTable value={cfg.campos} size="small" emptyMessage="Sin campos adicionales" rowHover onRowClick={(e) => setCampo({ ...e.data })}
                        style={{ cursor: 'pointer' }} responsiveLayout="scroll">
                        <Column field="paso" header="Paso" />
                        <Column field="etiqueta" header="Etiqueta" />
                        <Column field="tipo" header="Tipo" />
                        <Column header="Obligatorio" body={(r) => (r.obligatorio ? 'Si' : 'No')} />
                        <Column header="Activo" body={(r) => (r.activo ? 'Si' : 'No')} />
                    </DataTable>
                </TabPanel>
            </TabView>

            <Dialog header={campo && campo.campo_id ? 'Editar campo' : 'Nuevo campo'} visible={!!campo} style={{ width: 'min(30rem, 95vw)' }} onHide={() => setCampo(null)}
                footer={<Button label="Guardar" icon="pi pi-save" onClick={() => guardar(async () => {
                    await poli(campo.campo_id ? `/config/campos/${campo.campo_id}` : '/config/campos', campo.campo_id ? 'PUT' : 'POST', campo);
                    setCampo(null);
                }, 'Campo guardado')} />}>
                {campo && (
                    <div className="flex flex-column gap-3">
                        <label htmlFor="campo-etiqueta">Etiqueta</label>
                        <InputText id="campo-etiqueta" value={campo.etiqueta || ''} onChange={(e) => setCampo({ ...campo, etiqueta: e.target.value })} />
                        <label htmlFor="campo-paso">Paso</label>
                        <Dropdown inputId="campo-paso" value={campo.paso} options={PASOS} onChange={(e) => setCampo({ ...campo, paso: e.value })} />
                        <label htmlFor="campo-tipo">Tipo</label>
                        <Dropdown inputId="campo-tipo" value={campo.tipo} options={TIPOS_CAMPO} onChange={(e) => setCampo({ ...campo, tipo: e.value })} />
                        {campo.tipo === 'LISTA' && (
                            <>
                                <label htmlFor="campo-opciones">Opciones (separadas por ;)</label>
                                <InputText id="campo-opciones" value={campo.opciones || ''} onChange={(e) => setCampo({ ...campo, opciones: e.target.value })} />
                            </>
                        )}
                        <div className="flex align-items-center gap-2">
                            <Checkbox inputId="campo-obligatorio" checked={!!campo.obligatorio} onChange={(e) => setCampo({ ...campo, obligatorio: e.checked })} />
                            <label htmlFor="campo-obligatorio">Obligatorio</label>
                        </div>
                        <div className="flex align-items-center gap-2">
                            <Checkbox inputId="campo-activo" checked={campo.activo !== false} onChange={(e) => setCampo({ ...campo, activo: e.checked })} />
                            <label htmlFor="campo-activo">Activo</label>
                        </div>
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default PoliConfiguracion;
